import React, { useState, useMemo, useEffect } from 'react';
import api from '../../services/api';
import Header from './Header';
import FilterPanel from './FilterPanel';
import ProjectSummary from './ProjectSummary';
import DepartmentCards from './DepartmentCards';
import ProjectTable from './ProjectTable';
import ChartsSection from './ChartsSection';
import AIInsightsPanel from './AIInsightsPanel';

const IWMSDashboard = () => {
  const [filters, setFilters] = useState({
    financialYear: '2025-2026',  // Updated to match database format
    fundCategory: 'all', 
    fundHead: 'all'
  });
  
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedStatus, setSelectedStatus] = useState(''); // Add status selection state
  const [stageFilteredProjects, setStageFilteredProjects] = useState([]); // Add stage-filtered projects state
  const [projects, setProjects] = useState([]);
  const [departmentStats, setDepartmentStats] = useState([]);
  const [projectSummary, setProjectSummary] = useState(null);
  const [filterOptions, setFilterOptions] = useState({
    financialYears: [],
    fundCategories: [],
    fundHeads: [],
    projectStages: [],
    departments: []
  });
  const [availableFundCategories, setAvailableFundCategories] = useState([]); // Dynamic fund categories with counts
  const [availableFundHeads, setAvailableFundHeads] = useState([]); // Dynamic fund heads based on category
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load filter options on component mount
  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        setLoading(true);
        // Load filter options from IWMS service (your exact business logic)
        const options = await api.iwms.getFilterOptions();
        setFilterOptions(options);
        
        // Load initial fund heads (all)
        const fundHeads = await api.iwms.getFundHeads();
        setAvailableFundHeads(fundHeads);
      } catch (err) {
        console.error('Failed to load filter options:', err);
        setError('Failed to load filter options');
      } finally {
        setLoading(false);
      }
    };

    loadFilterOptions();
  }, []);

  // Load fund categories with counts based on current filters
  useEffect(() => {
    const loadFundCategories = async () => {
      try {
        // Convert frontend filters to backend format for categories
        const backendFilters = {
          financial_year: filters.financialYear,
          fund_head: filters.fundHead,
          department: selectedDepartment || undefined
        };

        const categories = await api.iwms.getFundCategories(backendFilters);
        setAvailableFundCategories(categories);
      } catch (err) {
        console.error('Failed to load fund categories with counts:', err);
      }
    };

    // Skip if filter options aren't loaded yet
    if (filterOptions.financialYears.length > 0) {
      loadFundCategories();
    }
  }, [filters.financialYear, filters.fundHead, selectedDepartment, filterOptions.financialYears]);

  // Load fund heads when fund category changes (cascading dropdown)
  useEffect(() => {
    const loadFundHeads = async () => {
      try {
        // Convert frontend filters to backend format for fund heads  
        const backendFilters = {
          financial_year: filters.financialYear,
          fund_category: filters.fundCategory,
          department: selectedDepartment || undefined
        };

        const fundHeads = await api.iwms.getFundHeads(filters.fundCategory, backendFilters);
        setAvailableFundHeads(fundHeads);
        
        // Reset fund head selection if current selection is not available in new category
        if (filters.fundHead !== 'all') {
          const currentFundHeadExists = fundHeads.some(head => head.id == filters.fundHead);
          if (!currentFundHeadExists) {
            setFilters(prev => ({ ...prev, fundHead: 'all' }));
          }
        }
      } catch (err) {
        console.error('Failed to load fund heads:', err);
      }
    };

    // Skip if filter options aren't loaded yet
    if (filterOptions.financialYears.length > 0) {
      loadFundHeads();
    }
  }, [filters.fundCategory, filters.financialYear, selectedDepartment, filterOptions.financialYears]);

  // Load projects and department stats whenever filters change
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // Convert frontend filters to backend format
        const backendFilters = {
          financial_year: filters.financialYear,
          fund_category: filters.fundCategory,
          fund_head: filters.fundHead,
          department: selectedDepartment || undefined
        };

        console.log('Loading IWMS data with filters:', backendFilters);

        // Load data using IWMS service (your exact business logic)
        const [projectsData, departmentData, summaryData] = await Promise.all([
          api.iwms.getProjects(backendFilters),
          api.iwms.getDepartmentStats(backendFilters),
          api.iwms.getProjectSummary(backendFilters)
        ]);

        console.log('IWMS data loaded successfully:', {
          projects: projectsData.projects?.length || 0,
          departments: departmentData.departments?.length || 0,
          summary: summaryData
        });

        setProjects(projectsData.projects || []);
        setDepartmentStats(departmentData.departments || []);
        setProjectSummary(summaryData);
        setError(null);
      } catch (err) {
        console.error('Failed to load IWMS data:', err);
        setError('Failed to load dashboard data. Please try refreshing the page.');
      } finally {
        setLoading(false);
      }
    };

    // Load data after a short delay to ensure filter options are loaded
    const timeoutId = setTimeout(loadData, 100);
    return () => clearTimeout(timeoutId);
  }, [filters, selectedDepartment]);

  const handleFilterChange = (filterKey, value) => {
    setFilters(prev => ({
      ...prev,
      [filterKey]: value
    }));
    // Reset selected department and status when filters change
    setSelectedDepartment('');
    setSelectedStatus('');
    setStageFilteredProjects([]);
  };

  const handleResetFilters = () => {
    setFilters({
      financialYear: '2025-2026',  // Reset to match database format
      fundCategory: 'all', 
      fundHead: 'all'
    });
    setSelectedDepartment('');
    setSelectedStatus('');
    setStageFilteredProjects([]);
  };

  // Get projects for selected department
  const selectedDepartmentProjects = useMemo(() => {
    if (!selectedDepartment) return [];
    return projects.filter(p => p.department === selectedDepartment);
  }, [projects, selectedDepartment]);

  const handleDepartmentClick = (department) => {
    setSelectedDepartment(department === selectedDepartment ? '' : department);
    setSelectedStatus(''); // Clear status selection when department is selected
    setStageFilteredProjects([]); // Clear stage-filtered projects when department is selected
    
    // Auto-scroll to project table section after a brief delay
    if (department !== selectedDepartment) {
      setTimeout(() => {
        const projectTableSection = document.getElementById('project-table-section');
        if (projectTableSection) {
          projectTableSection.scrollIntoView({ 
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 100);
    }
  };

  const handleStatusClick = async (status) => {
    // Toggle selection - if clicking the same status, clear it
    if (status === selectedStatus) {
      setSelectedStatus('');
      setStageFilteredProjects([]);
      return;
    }
    
    setSelectedStatus(status);
    setSelectedDepartment(''); // Clear department selection when status is selected
    
    try {
      // Convert frontend filters to backend format
      const backendFilters = {
        financial_year: filters.financialYear,
        fund_category: filters.fundCategory,
        fund_head: filters.fundHead
      };

      let stageProjects = [];
      
      // Fetch projects based on stage using backend API calls
      if (status === 'Total') {
        stageProjects = projects; // Use already loaded projects
      } else if (status === 'Proposed') {
        // Fetch projects in Proposed Works stage
        const response = await api.iwms.getProjects({
          ...backendFilters,
          project_stage: 'Proposed Works'
        });
        stageProjects = response.projects || [];
      } else if (status === 'Work Order') {
        // Fetch projects in Work Orders stage
        const response = await api.iwms.getProjects({
          ...backendFilters,
          project_stage: 'Work Orders'
        });
        stageProjects = response.projects || [];
      } else if (status === 'Completed') {
        // Fetch projects in Work Completion stage
        const response = await api.iwms.getProjects({
          ...backendFilters,
          project_stage: 'Work Completion'
        });
        stageProjects = response.projects || [];
      } else if (status === 'Remaining') {
        // For remaining projects, we need to calculate the difference
        // Get all projects and subtract the main stage projects
        const [proposedResponse, workOrderResponse, completedResponse] = await Promise.all([
          api.iwms.getProjects({ ...backendFilters, project_stage: 'Proposed Works' }),
          api.iwms.getProjects({ ...backendFilters, project_stage: 'Work Orders' }),
          api.iwms.getProjects({ ...backendFilters, project_stage: 'Work Completion' })
        ]);
        
        const mainStageProjectIds = new Set([
          ...(proposedResponse.projects || []).map(p => p.id),
          ...(workOrderResponse.projects || []).map(p => p.id),
          ...(completedResponse.projects || []).map(p => p.id)
        ]);
        
        stageProjects = projects.filter(p => !mainStageProjectIds.has(p.id));
      } else {
        // For other statuses, use status-based filtering
        stageProjects = projects.filter(p => p.status === status);
      }
      
      setStageFilteredProjects(stageProjects);
      
      // Auto-scroll to project table section after a brief delay
      setTimeout(() => {
        const projectTableSection = document.getElementById('project-table-section');
        if (projectTableSection) {
          projectTableSection.scrollIntoView({ 
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 100);
      
    } catch (error) {
      console.error('Failed to fetch stage-filtered projects:', error);
      setStageFilteredProjects([]);
    }
  };

  if (loading && !projects.length && !departmentStats.length) {
    // Show full-screen loading only on initial load (when no data exists)
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading IWMS Dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50">
      <div className="container mx-auto px-4 py-6 space-y-6">
        {/* Header */}
        <Header />

        {/* Filter Panel */}
        <FilterPanel 
          filters={filters}
          onFilterChange={handleFilterChange}
          onResetFilters={handleResetFilters}
          filterOptions={{
            ...filterOptions,
            fundCategories: availableFundCategories, // Use dynamic fund categories with counts
            fundHeads: availableFundHeads // Use dynamic fund heads with counts
          }}
        />

        {/* Subtle Loading Indicator for Filter Changes */}
        {loading && (
          <div className="mb-6 bg-white rounded-lg border-2 border-slate-200 p-4 shadow-sm">
            <div className="flex items-center justify-center space-x-3">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-teal-600"></div>
              <p className="text-slate-600 font-medium">Updating dashboard data...</p>
            </div>
          </div>
        )}

        {/* Main Dashboard Content with subtle loading state */}
        <div className={`transition-opacity duration-300 space-y-8 ${loading ? 'opacity-75' : 'opacity-100'}`}>
          {/* Project Summary */}
          <div className="mb-8">
            <ProjectSummary 
              totalProjects={projects.length}
              projects={projects}
              projectSummary={projectSummary}
              onStatusClick={handleStatusClick}
            />
          </div>

          {/* Department Cards */}
          <div className="mb-8">
            <DepartmentCards 
              departmentStats={departmentStats}
              onDepartmentClick={handleDepartmentClick}
              selectedDepartment={selectedDepartment}
            />
          </div>

          {/* Project Table Section - Shows filtered projects */}
          {(selectedDepartment || selectedStatus) && (
            <div id="project-table-section" className="mb-8">
              <ProjectTable 
                projects={selectedDepartment ? selectedDepartmentProjects : stageFilteredProjects}
                selectedDepartment={selectedDepartment}
                selectedStatus={selectedStatus}
                title={selectedDepartment ? `${selectedDepartment} Department Projects` : `${selectedStatus} Projects`}
              />
            </div>
          )}

          {/* Charts Section */}
          <div className="mb-8">
            <ChartsSection 
              departmentStats={departmentStats}
              projects={projects}
            />
          </div>

          {/* AI Insights Panel */}
          <div className="mb-8">
            <AIInsightsPanel 
              projects={projects}
              departmentStats={departmentStats}
              projectSummary={projectSummary}
              filters={filters}
              selectedDepartment={selectedDepartment}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default IWMSDashboard;